import { useQuery, useMutation, useQueryClient } from 'react-query';
import { EmpresasAdminService } from '../services';
import type { 
  EmpresasFiltros, 
  CreateEmpresaRequest, 
  UpdateEmpresaRequest,
  AsignarUsuarioRequest,
  UpdateUsuarioEmpresaRequest,
  UsuariosFiltros
} from '../types';

export const useEmpresasAdmin = (filtros: EmpresasFiltros = {}) => {
  return useQuery(
    ['empresas-admin', filtros],
    () => EmpresasAdminService.obtenerEmpresas(filtros),
    {
      keepPreviousData: true,
      staleTime: 30000,
      retry: (failureCount, error: any) => {
        // No reintentar en errores de autenticación/autorización
        if (error?.response?.status === 401 || error?.response?.status === 403) {
          return false;
        }
        return failureCount < 2;
      },
      onError: (error: any) => {
        console.error('Error al obtener empresas:', error);
        // No invalidar sesión para errores de permisos
        if (error?.response?.status === 403) {
          console.warn('⚠️ Usuario sin permisos para administración de empresas');
        }
      }
    }
  );
};

export const useEmpresaAdmin = (empresaId: number, enabled = true) => {
  return useQuery(
    ['empresa-admin', empresaId],
    () => EmpresasAdminService.obtenerEmpresa(empresaId),
    {
      enabled: enabled && !!empresaId,
      staleTime: 60000,
      retry: (failureCount, error: any) => {
        // No reintentar en errores de autenticación/autorización
        if (error?.response?.status === 401 || error?.response?.status === 403) {
          return false;
        }
        return failureCount < 2;
      },
      onError: (error: any) => {
        console.error(`Error al obtener empresa ${empresaId}:`, error);
        if (error?.response?.status === 403) {
          console.warn('⚠️ Usuario sin permisos para ver esta empresa');
        }
      }
    }
  );
};

export const useEstadisticasEmpresaAdmin = (empresaId: number, enabled = true) => {
  return useQuery(
    ['estadisticas-empresa', empresaId],
    () => EmpresasAdminService.obtenerEstadisticasEmpresa(empresaId),
    {
      enabled: enabled && !!empresaId,
      staleTime: 300000,
      retry: 2,
      onError: (error) => {
        console.error(`Error al obtener estadisticas de empresa ${empresaId}:`, error);
      }
    }
  );
};

export const useUsuariosEmpresaAdmin = (empresaId: number, enabled = true) => {
  return useQuery(
    ['usuarios-empresa', empresaId],
    () => EmpresasAdminService.obtenerUsuariosEmpresa(empresaId),
    {
      enabled: enabled && !!empresaId,
      staleTime: 30000,
      retry: 2,
      onError: (error) => {
        console.error(`Error al obtener usuarios de empresa ${empresaId}:`, error);
      }
    }
  );
};

export const useBuscarUsuariosAdmin = (filtros: UsuariosFiltros, enabled = true) => {
  return useQuery(
    ['buscar-usuarios', filtros],
    () => EmpresasAdminService.buscarUsuarios(filtros),
    {
      enabled: enabled && !!(filtros.busqueda || filtros.tiene_acceso_mava !== undefined),
      keepPreviousData: true,
      staleTime: 30000,
      retry: 2,
      onError: (error) => {
        console.error('Error al buscar usuarios:', error);
      }
    }
  );
};

export const useCrearEmpresaAdmin = () => {
  const queryClient = useQueryClient();
  
  return useMutation(
    (datos: CreateEmpresaRequest) => EmpresasAdminService.crearEmpresa(datos),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['empresas-admin']);
        queryClient.invalidateQueries(['estadisticas-empresa']);
      },
      onError: (error) => {
        console.error('Error al crear empresa:', error);
      }
    }
  );
};

export const useActualizarEmpresaAdmin = () => {
  const queryClient = useQueryClient();
  
  return useMutation(
    ({ empresaId, datos }: { empresaId: number; datos: UpdateEmpresaRequest }) => 
      EmpresasAdminService.actualizarEmpresa(empresaId, datos),
    {
      onSuccess: (_, { empresaId }) => {
        queryClient.invalidateQueries(['empresas-admin']);
        queryClient.invalidateQueries(['empresa-admin', empresaId]);
        queryClient.invalidateQueries(['estadisticas-empresa', empresaId]);
      },
      onError: (error) => {
        console.error('Error al actualizar empresa:', error);
      }
    }
  );
};

export const useDesactivarEmpresaAdmin = () => {
  const queryClient = useQueryClient();
  
  return useMutation(
    (empresaId: number) => EmpresasAdminService.desactivarEmpresa(empresaId),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['empresas-admin']);
      },
      onError: (error) => {
        console.error('Error al desactivar empresa:', error);
      }
    }
  );
};

export const useAsignarUsuarioAdmin = () => {
  const queryClient = useQueryClient();
  
  return useMutation(
    ({ empresaId, datos }: { empresaId: number; datos: AsignarUsuarioRequest }) => 
      EmpresasAdminService.asignarUsuario(empresaId, datos),
    {
      onSuccess: (_, { empresaId }) => {
        queryClient.invalidateQueries(['usuarios-empresa', empresaId]);
        queryClient.invalidateQueries(['empresas-admin']);
        queryClient.invalidateQueries(['buscar-usuarios']);
      },
      onError: (error) => {
        console.error('Error al asignar usuario:', error);
      }
    }
  );
};

export const useActualizarUsuarioEmpresaAdmin = () => {
  const queryClient = useQueryClient();
  
  return useMutation(
    ({ empresaId, usuarioId, datos }: { 
      empresaId: number; 
      usuarioId: number; 
      datos: UpdateUsuarioEmpresaRequest 
    }) => EmpresasAdminService.actualizarUsuarioEmpresa(empresaId, usuarioId, datos),
    {
      onSuccess: (_, { empresaId }) => {
        queryClient.invalidateQueries(['usuarios-empresa', empresaId]);
        queryClient.invalidateQueries(['empresas-admin']);
      },
      onError: (error) => {
        console.error('Error al actualizar usuario de empresa:', error);
      }
    }
  );
};

export const useDesasignarUsuarioAdmin = () => {
  const queryClient = useQueryClient();
  
  return useMutation(
    ({ empresaId, usuarioId }: { empresaId: number; usuarioId: number }) => 
      EmpresasAdminService.desasignarUsuario(empresaId, usuarioId),
    {
      onSuccess: (_, { empresaId }) => {
        queryClient.invalidateQueries(['usuarios-empresa', empresaId]);
        queryClient.invalidateQueries(['empresas-admin']);
        queryClient.invalidateQueries(['buscar-usuarios']);
      },
      onError: (error) => {
        console.error('Error al desasignar usuario:', error);
      }
    }
  );
};

export const useGestionEmpresasAdmin = (filtrosIniciales: EmpresasFiltros = {}) => {
  const empresasQuery = useEmpresasAdmin(filtrosIniciales);
  const crearMutation = useCrearEmpresaAdmin();
  const actualizarMutation = useActualizarEmpresaAdmin();
  const desactivarMutation = useDesactivarEmpresaAdmin();
  
  return {
    empresas: empresasQuery.data,
    loading: empresasQuery.isLoading,
    error: empresasQuery.error,
    refetch: empresasQuery.refetch,
    crear: crearMutation.mutateAsync,
    actualizar: actualizarMutation.mutateAsync,
    desactivar: desactivarMutation.mutateAsync,
    creando: crearMutation.isLoading,
    actualizando: actualizarMutation.isLoading,
    desactivando: desactivarMutation.isLoading,
    errorCrear: crearMutation.error,
    errorActualizar: actualizarMutation.error,
    errorDesactivar: desactivarMutation.error
  };
};

export const useValidacionesAdmin = () => {
  return {
    validarRut: EmpresasAdminService.validarRut,
    validarEmail: EmpresasAdminService.validarEmail,
    validarTelefono: EmpresasAdminService.validarTelefono
  };
};
